import React, { useMemo, useState } from 'react';
import './CompetitorFunnelOverTimeChart.scss';
import { useTranslation } from 'react-i18next';
import type {
  InsightsBrandFunnelTimeSeriesBucket,
  InsightsBrandFunnelsResponse,
} from '@brand24-original/llm-types';
import { AllBrandsStatsResponse } from '../../../../shared/models/projects/stats/AllBrandsStatsResponse';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import { brandColorOf } from '../../../../shared/components/report/brandColors';
import { PeriodType } from '../../../../shared/types/prompt/PeriodType';
import { percentOf } from '../../helpers/overviewInsightHelpers';
import { useBrandSelection } from '../../hooks/useBrandSelection';
import BrandPicker from '../brand-picker/BrandPicker';
import MetricSwitcher from '../../../../shared/components/metric-switcher/MetricSwitcher';
import InsightsTrendChart, { InsightsTrendSeries } from '../insights-trend-chart/InsightsTrendChart';

// The funnel stages only — no Cited (user decision): the chart compares brands on the funnel, and
// per-brand cited over time is not in the payload. Labels shared with the prompt matrix switcher.
const METRICS = [
  { key: 'visible', labelKey: 'insightPage.promptMatrix.metricVisibility' },
  { key: 'top3', labelKey: 'insightPage.promptMatrix.metricTop3' },
  { key: 'top1', labelKey: 'insightPage.promptMatrix.metricTop1' },
] as const;

type MetricKey = (typeof METRICS)[number]['key'];

// Default lines: the analyzed brand + the top 4 by visibility (user decision) — more lines on one
// chart are unreadable; the picker still allows any number.
const DEFAULT_SELECTED_BRANDS = 5;

interface CompetitorFunnelOverTimeChartProps {
  buckets: InsightsBrandFunnelTimeSeriesBucket[] | null;
  period: PeriodType;
  /** Brand dictionary (labels, focusBrandKey) + picker base — the brand-funnels payload of the page. */
  brandFunnels: InsightsBrandFunnelsResponse | null;
  sovBrands: AllBrandsStatsResponse[] | null;
  isLoading: boolean;
  isError?: boolean;
}

/**
 * "Competitor funnel over time": one funnel stage (visibility / Top 3 / Top 1, switchable) per time
 * bucket for the brands ticked in the picker — a line per brand in the REPORT brand colors (the same
 * map as the picker rows and the matrix headers), on the shared InsightsTrendChart body.
 */
const CompetitorFunnelOverTimeChart: React.FC<CompetitorFunnelOverTimeChartProps> = ({
  buckets,
  period,
  brandFunnels,
  sovBrands,
  isLoading,
  isError,
}) => {
  const { t } = useTranslation();
  const [metric, setMetric] = useState<MetricKey>('visible');
  const { focusBrandKey, pickerBrands, selectedKeys, selectedBrands, brandColors, selectBrands } =
    useBrandSelection(brandFunnels, sovBrands, DEFAULT_SELECTED_BRANDS);

  const series: InsightsTrendSeries[] = useMemo(() => {
    const data = buckets ?? [];
    // Cells are sparse (a brand absent from a bucket = 0) — index them per bucket once.
    const cellsPerBucket = data.map(
      (bucket) => new Map(bucket.brands.map((cell) => [cell.brandKey, cell]))
    );
    return selectedBrands.map((brand, index) => ({
      name: brand.label,
      color: brandColorOf(brandColors, brand.label, brand.brandKey === focusBrandKey, index),
      // Lines only: up to ten overlapping area fills would hide each other.
      areaAlpha: 0,
      data: data.map((bucket, i) =>
        percentOf(cellsPerBucket[i].get(brand.brandKey)?.[metric] ?? 0, bucket.total)
      ),
    }));
  }, [buckets, selectedBrands, brandColors, focusBrandKey, metric]);

  const hasData = !!buckets && buckets.length > 0 && selectedBrands.length > 0;

  return (
    <div className="stats__panel competitor-funnel-over-time-chart">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('insightPage.competitorFunnelOverTime.title')}
          <CustomTooltip tooltipText="insightPage.competitorFunnelOverTime.tooltip" addMargin={false} />
        </div>
        {!isLoading && !isError && hasData && (
          <div className="competitor-funnel-over-time-chart__controls">
            <BrandPicker
              brands={pickerBrands}
              focusBrandKey={focusBrandKey}
              selectedKeys={selectedKeys}
              brandColors={brandColors}
              onChange={selectBrands}
            />
            <MetricSwitcher options={METRICS} value={metric} onChange={setMetric} />
          </div>
        )}
      </div>
      <div className="insights-trend-chart__card">
        {isLoading ? (
          <div className="insights-trend-chart__skeleton" />
        ) : isError ? (
          <EmptyPanel textKey="somethingWentWrong" />
        ) : !hasData ? (
          <EmptyPanel />
        ) : (
          <InsightsTrendChart buckets={buckets} period={period} series={series} />
        )}
      </div>
    </div>
  );
};

export default CompetitorFunnelOverTimeChart;
